/* eslint-disable */
import {Box, Flex} from '@indoqa/style-system'
import {useQueryParam} from '@oegbv/ui-shared'
import querystring from 'query-string'
import * as React from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {Observable} from 'rxjs'
import {ajax} from 'rxjs/ajax'
import {AlignedContentPanel} from '../../commons/components/content/AlignedContentPanel'
import {ContentPanel} from '../../commons/components/content/ContentPanel'
import {ReaderFooterPanel} from '../../commons/components/footer/ReaderFooterPanel'
import {DigitalHelmet} from '../../commons/components/helmet/DigitalHelmet'
import {ReaderLayout} from '../../commons/layouts/reader-layout/ReaderLayout'
import {getHeaders} from '../../commons/store/service-utils'
import {ContentNode} from '../../commons/types/ContentNode'
import {StructureNode} from '../../commons/types/Structure'
import {MissingCredentialsErrorPage} from '../../error/pages/MissingCredentialsErrorPage'
import {NotAvailableErrorPage} from '../../error/pages/NotAvailableErrorPage'
import {SearchNavigationBar} from '../../kv/components/search-bar/SearchNavigationBar'
import {BookWebbookSlugReaderHeaderPanel} from '../components/webbook-slug/header/BookWebbooSlugReaderHeaderPanel'
import {BookWebbookSlugStructurePanel} from '../components/webbook-slug/structure/BookWebbookSlugStructurePanel'
import {bookPathWebBookSlug, bookPathWebBookSlugId, bookQueryParams} from '../store/book.paths'

interface LocationParams {
  bookSlug: string
  chapterSlug?: string
}

interface StructureNodeHierarchy {
  node: StructureNode
  hierarchy: StructureNode[]
}

enum LoadingState {
  LOADING,
  SUCCESS,
  ERROR,
  NOT_AVAILABLE,
  FORBIDDEN,
  NO_CHAPTERS,
}

export function BookWebBookSlugReaderPage() {
  const history = useHistory()

  const {bookSlug, chapterSlug} = useParams<LocationParams>()
  const [loadingState, setLoadingState] = React.useState(LoadingState.LOADING)
  const [structure, setStructure] = React.useState<StructureNode | null>(null)
  const [loadedChapterSlug, setLoadedChapterSlug] = React.useState<string | null>(null)
  const [chapterContent, setChapterContent] = React.useState<ContentNode | null>(null)

  const canonicalUrl = chapterSlug ? bookPathWebBookSlug(bookSlug, chapterSlug) : bookPathWebBookSlugId(bookSlug)

  const currentNode = React.useMemo(() => findCurrentNode(structure, chapterSlug), [structure, chapterSlug])
  const paraId = currentNode?.node.link.id
  const chapterNode = React.useMemo(() => getChapterFromCurrentNode(currentNode), [currentNode])

  const userQuery = useQueryParam(bookQueryParams.userQuery) || undefined
  const previousUserQueryRef = React.useRef<string | null | undefined>(null)

  const contentRef = React.createRef<HTMLDivElement>()
  React.useEffect(() => {
    if (structure !== null && userQuery === previousUserQueryRef.current) {
      return
    }
    loadStructure$(bookSlug, userQuery).subscribe(
      (structureNode) => setStructure(structureNode),
      (e) => {
        if (e.status === 401) {
          setLoadingState(LoadingState.FORBIDDEN)
        } else {
          setLoadingState(LoadingState.NOT_AVAILABLE)
        }
        previousUserQueryRef.current = userQuery
      }
    )
  }, [userQuery])

  // console.log({
  //   paraId,
  //   structure,
  //   chapterSlug,
  //   chapterNode,
  //   loadedChapterSlug,
  //   currentNode,
  //   chapterContent,
  // })
  React.useEffect(() => {
    if (structure === null) {
      return
    }
    console.log('Loading structure', chapterNode, chapterSlug)
    if (
      chapterNode !== null &&
      chapterNode.slug &&
      (chapterNode.slug !== loadedChapterSlug || userQuery !== previousUserQueryRef.current)
    ) {
      setLoadedChapterSlug(chapterNode.slug)
      loadChapter$(bookSlug, chapterNode.slug, userQuery).subscribe(
        (node) => {
          setLoadingState(LoadingState.SUCCESS)
          setChapterContent(node)
        },
        () => {
          setLoadingState(LoadingState.NOT_AVAILABLE)
        }
      )
    } else {
      if (userQuery) {
        setLoadingState(LoadingState.NO_CHAPTERS)
      } else {
        setLoadingState(LoadingState.NOT_AVAILABLE)
      }
    }
  }, [structure, chapterNode])

  if (loadingState === LoadingState.FORBIDDEN) {
    return <MissingCredentialsErrorPage url={window.location.href} />
  } else if (loadingState === LoadingState.NOT_AVAILABLE) {
    return <NotAvailableErrorPage />
  } else if (loadingState === LoadingState.ERROR) {
    return <div>Error</div>
  }

  const searchNavigationBar =
    userQuery && loadingState !== LoadingState.NO_CHAPTERS ? (
      <SearchNavigationBar
        structure={structure}
        paraId={paraId}
        goToHit={(hit) => {
          if (hit.chapterSlug) {
            history.push(bookPathWebBookSlug(bookSlug, hit.chapterSlug, {userQuery}))
          }
        }}
        contentRef={contentRef}
      />
    ) : undefined

  const title = (structure as unknown as any)?.properties?.quelle_kurz
  const header = (
    <BookWebbookSlugReaderHeaderPanel
      bookSlug={bookSlug}
      bookTitle={title}
      chapterSlug={chapterSlug!}
      loading={loadingState === LoadingState.LOADING}
      userQuery={userQuery}
    />
  )
  const footer = <ReaderFooterPanel />
  const nav = (
    <BookWebbookSlugStructurePanel structure={structure} bookSlug={bookSlug} paraId={paraId} userQuery={userQuery} />
  )
  return (
    <ReaderLayout header={header} nav={nav} searchNavigationBar={searchNavigationBar} footer={footer}>
      <DigitalHelmet title={structure ? structure.link.name : ''} canonical={canonicalUrl} />
      <AlignedContentPanel navShown={true} maxWidth={1000}>
        {loadingState !== LoadingState.NO_CHAPTERS && (
          <ContentPanel
            key={chapterSlug}
            ref={contentRef}
            contentNode={chapterContent}
            currentNodeId={{id: paraId || ''}}
            enableLawLinks
            disablePrinting
          />
        )}
        {loadingState === LoadingState.NO_CHAPTERS && (
          <Flex fullWidth fullHeight center>
            <Box style={{marginTop: '20vh'}}>
              Keine Ergebnisse gefunden.
              <br /> Bitte versuchen Sie eine andere Eingabe.
            </Box>
          </Flex>
        )}
      </AlignedContentPanel>
    </ReaderLayout>
  )
}

const loadStructure$ = (bookIdOrSlug: string, userQuery: string | null | undefined): Observable<StructureNode> => {
  const queryParams: {[key: string]: string | boolean | number} = {}
  if (userQuery) {
    queryParams['prune'] = true
    queryParams['query'] = userQuery
  }
  const data = querystring.stringify(queryParams)
  return ajax.getJSON(`/api/v1/book/${bookIdOrSlug}/structure${data ? '?' + data : ''}`, getHeaders())
}

const loadChapter$ = (
  bookIdOrSlug: string,
  chapterSlug: string,
  userQuery: string | null | undefined
): Observable<ContentNode> => {
  const queryParams: {[key: string]: string | boolean | number} = {}
  queryParams['include-children'] = true
  if (userQuery) {
    queryParams['query'] = userQuery
  }
  return ajax.getJSON(
    `/api/v1/book/${bookIdOrSlug}/chapters/${chapterSlug}?${querystring.stringify(queryParams)}`,
    getHeaders()
  )
}
const findCurrentNode = (
  structure: StructureNode | null,
  chapterSlug?: string | null
): StructureNodeHierarchy | null => {
  if (!structure) {
    return null
  }
  if (!chapterSlug) {
    return findFirstChapter(structure)
  }

  const stack: StructureNodeHierarchy[] = [
    {
      node: structure,
      hierarchy: [],
    },
  ]
  const parentMap = new Map<StructureNode, StructureNode | null>()
  parentMap.set(structure, null)

  while (stack.length > 0) {
    const element = stack.pop()

    if (!element) {
      continue
    }

    if (element.node.slug === chapterSlug) {
      return element
    }

    if (element.node.children) {
      for (const child of element.node.children) {
        stack.push({
          node: child,
          hierarchy: [...element.hierarchy, element.node],
        })
      }
    }
  }

  return null
}

const findFirstChapter = (structure: StructureNode): StructureNodeHierarchy | null => {
  const chapters = structure.children
  if (!chapters || chapters.length < 1) {
    return null
  }
  return {
    node: chapters[0],
    hierarchy: [structure],
  }
}

const getChapterFromCurrentNode = (nodeHierarchy?: StructureNodeHierarchy | null): StructureNode | null => {
  if (!nodeHierarchy) {
    return null
  }
  if (nodeHierarchy.hierarchy.length === 1) {
    return nodeHierarchy.node
  }
  return nodeHierarchy.hierarchy[1]
}
